/* eslint-disable import/prefer-default-export */
import axios from '../axios';

// ---------------------------------------------------------------------------
// Financeiro / Credenciais de integração — Service Layer (handoff 0079)
//
// ⚠️ REGRA INEGOCIÁVEL: o front NUNCA vê a chave de API.
// Ela é cifrada at-rest no backend e nenhuma resposta a devolve — nem em claro,
// nem cifrada. O que existe para exibir é `api_key_mask` ($aact_…9f3c),
// `key_status`, `environment` e `key_last_rotated_at`.
//
// Consequência para esta camada: `api_key` é WRITE-ONLY. Vai no corpo do POST e
// some. Nada de guardar em estado global, localStorage ou cache; nada de logar
// no console ou mandar para telemetria. Não existe "editar a chave" — existe
// ROTACIONAR.
//
// Listagem/criação são aninhadas na conta; o ciclo de vida (rotate/revoke/
// activate/test) age pelo ID DA CREDENCIAL, não pelo da conta.
// ---------------------------------------------------------------------------

// Uma conta pode ter várias credenciais ao longo do tempo, mas no máximo uma
// ATIVA por provedor (garantido no banco). As revogadas seguem listadas como
// histórico.
export async function listCredentials(bankAccountId) {
   const response = await axios.get(
      `/finance/bank-accounts/${bankAccountId}/credentials`,
   );
   return response?.data?.data?.credentials ?? [];
}

// { provider?, environment, apiKey } — `provider` default 'asaas' (hoje o
// único). O backend faz trim na chave, então colar com espaço/quebra de linha
// no fim (erro comum ao copiar do painel da ASAAS) não quebra.
export async function createCredential(bankAccountId, { provider, environment, apiKey }) {
   const body = { environment, api_key: apiKey };
   if (provider) body.provider = provider;

   const response = await axios.post(
      `/finance/bank-accounts/${bankAccountId}/credentials`,
      body,
   );
   return {
      credential: response?.data?.data?.credential ?? null,
      message: response?.data?.message ?? 'Credencial cadastrada com sucesso.',
   };
}

// Troca a chave de uma credencial ATIVA (o usuário gerou outra no painel da
// ASAAS). Credencial revogada não rotaciona → 422 `credential_not_active`.
export async function rotateCredential(credentialId, apiKey) {
   const response = await axios.post(
      `/finance/credentials/${credentialId}/rotate`,
      { api_key: apiKey },
   );
   return {
      credential: response?.data?.data?.credential ?? null,
      message: response?.data?.message ?? 'Chave rotacionada com sucesso.',
   };
}

// Bloqueia emissão NOVA por esta credencial. Não invalida nada que já existe —
// a chave segue válida na ASAAS até ser trocada lá. `reason` é opcional (3..255).
export async function revokeCredential(credentialId, reason) {
   const body = {};
   const trimmed = String(reason ?? '').trim();
   if (trimmed) body.reason = trimmed;

   const response = await axios.patch(
      `/finance/credentials/${credentialId}/revoke`,
      body,
   );
   return {
      credential: response?.data?.data?.credential ?? null,
      message: response?.data?.message ?? 'Credencial revogada.',
   };
}

// Reativa uma revogada. Falha se já houver outra ativa na conta (409) ou se a
// conta estiver inativa (422 `bank_account_inactive`).
export async function activateCredential(credentialId) {
   const response = await axios.patch(
      `/finance/credentials/${credentialId}/activate`,
   );
   return {
      credential: response?.data?.data?.credential ?? null,
      message: response?.data?.message ?? 'Credencial reativada.',
   };
}

// ⚠️ Devolve 200 SEMPRE que o teste roda — inclusive com chave recusada.
// Quem decide é `test.ok`, nunca o status HTTP. 4xx/5xx aqui significam outra
// coisa (404 = credencial inexistente; 5xx = o MWA falhou), e aí o erro sobe
// para o catch da página normalmente.
export async function testConnection(credentialId) {
   const response = await axios.post(
      `/finance/credentials/${credentialId}/test-connection`,
   );
   return response?.data?.data?.test ?? null;
}
